import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';

import { createCheckBatch } from '../../endpoints/financePayments';
import type { CreateCheckBatchResponse } from '../../types/financePayments';
import { getApiErrorMessage } from '../../utils/httpClientTs';
import { useQueryClient } from '@tanstack/react-query';

const BATCH_NUMBER_MAX_LEN = 32;
const BATCH_NUMBER_PATTERN = /^[A-Za-z0-9._-]+$/;

export const CHECK_BATCH_LIST_QUERY_KEYS = [
  ['finance-payments-batches'],
  ['finance-payments-pending-by-source'],
] as const;

interface CreateCheckBatchDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (res: CreateCheckBatchResponse) => void;
  /** Override create API (ELF Payments passes createElfCheckBatch). */
  createBatch?: (body?: {
    batch_number?: string;
  }) => Promise<CreateCheckBatchResponse>;
  /** Override react-query keys to invalidate after create. */
  listQueryKeys?: readonly (readonly string[])[];
}

export default function CreateCheckBatchDialog({
  open,
  onClose,
  onCreated,
  createBatch = createCheckBatch,
  listQueryKeys = CHECK_BATCH_LIST_QUERY_KEYS,
}: CreateCheckBatchDialogProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const [batchNumber, setBatchNumber] = useState('');
  const [creating, setCreating] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setBatchNumber('');
      setErrorMsg(null);
      setCreating(false);
    }
  }, [open]);

  const handleCreate = async (): Promise<void> => {
    setErrorMsg(null);
    const trimmed = batchNumber.trim();
    if (trimmed && !BATCH_NUMBER_PATTERN.test(trimmed)) {
      setErrorMsg(
        'Batch number may only contain letters, numbers, dashes, underscores, and periods.'
      );
      return;
    }
    setCreating(true);
    try {
      const res = await createBatch(trimmed ? { batch_number: trimmed } : {});
      for (const key of listQueryKeys) {
        await queryClient.invalidateQueries({ queryKey: [...key] });
      }
      onCreated?.(res);
      onClose();
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={creating ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>New check batch</DialogTitle>
      <DialogContent>
        {errorMsg && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setErrorMsg(null)}
          >
            {errorMsg}
          </Alert>
        )}
        <TextField
          autoFocus
          fullWidth
          label="Batch number (optional)"
          value={batchNumber}
          onChange={(e) => setBatchNumber(e.target.value)}
          inputProps={{ maxLength: BATCH_NUMBER_MAX_LEN }}
          helperText="Leave blank to auto-generate (e.g. B-20260303-abc123). Letters, numbers, dashes, underscores, and periods only."
          disabled={creating}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={creating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleCreate()}
          disabled={creating}
        >
          {creating ? 'Creating…' : 'Create batch'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
